// cursorEditorPopup — Classic 常設フロート「カーソル」(CursorEditorPopup) の
// 位置 pure helper (node vitest 対象). `PenPopup`/`penPopup.ts` と同設計
// (position model を UI 非依存に切り出し、CursorEditorPopup.tsx はそれを
// 組み合わせて描画するだけ)。
//
// PenPopup と異なり **可視状態は永続化しない** (= ephemeral、ClassicLayout が
// 普通の React state で開閉を持つ): カーソルエディタは「発見性を担保したい
// 常設ツール」ではなく、押した時だけ使う一時的な作業窓 (custom-cursor-2026-09-19.md
// §2.5 「エディタ」) なので、PenPopup の「初回 open 既定」のような設計は不要。

import type { SidebarDockSide } from './classicToolFloat.js';
import type { FloatingPopupPosition, StorageLike } from './layerPopup.js';

/** popup のデフォルト位置を出す際の画面端マージン (px、PenPopup/LayerPopup と揃える). */
export const CURSOR_EDITOR_POPUP_MARGIN = 16;

/** popup のおおよその幅 / 高さ (= デフォルト位置 / 初回 clamp fallback 用). */
export const CURSOR_EDITOR_POPUP_NOMINAL_WIDTH = 280;
export const CURSOR_EDITOR_POPUP_NOMINAL_HEIGHT = 420;

/**
 * popup のデフォルト位置 = 画面中央寄り (PenPopup/LayerPopup が右上/左上を
 * 占有しているため、カーソルエディタは中央に出して衝突を避ける)。
 *
 * `dockSide` はサイドバーのドック側 (`classicToolFloat.ts`) — サイドバーの
 * 常設グリップ帯と重ならないよう、`dockSide==='right'` のときは中央からやや
 * 左へ寄せる (PenPopup/LayerPopup と同じミラー方針)。
 */
export const defaultCursorEditorPopupPosition = (
  innerWidth: number,
  innerHeight: number,
  dockSide: SidebarDockSide = 'left',
): FloatingPopupPosition => {
  if (!Number.isFinite(innerWidth) || !Number.isFinite(innerHeight)) {
    return { x: CURSOR_EDITOR_POPUP_MARGIN, y: CURSOR_EDITOR_POPUP_MARGIN };
  }
  const centeredX = Math.max(
    CURSOR_EDITOR_POPUP_MARGIN,
    (innerWidth - CURSOR_EDITOR_POPUP_NOMINAL_WIDTH) / 2,
  );
  const centeredY = Math.max(
    CURSOR_EDITOR_POPUP_MARGIN,
    (innerHeight - CURSOR_EDITOR_POPUP_NOMINAL_HEIGHT) / 3,
  );
  if (dockSide === 'right') {
    return { x: Math.max(CURSOR_EDITOR_POPUP_MARGIN, centeredX - 120), y: centeredY };
  }
  return { x: centeredX, y: centeredY };
};

/** localStorage キー: popup 位置 (per-user 記憶, PenPopup と同流儀). */
export const CURSOR_EDITOR_POPUP_POS_KEY = 'oekaki:classic-cursor-editor-popup-pos';

/**
 * 保存済 position を読み出す. 不在 / 不正 JSON / storage 不可は `null`
 * (= caller がデフォルト位置に倒す). 例外は throw しない.
 */
export const readCursorEditorPopupPosition = (
  storage: StorageLike,
): FloatingPopupPosition | null => {
  try {
    const raw = storage.getItem(CURSOR_EDITOR_POPUP_POS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { x?: unknown }).x === 'number' &&
      typeof (parsed as { y?: unknown }).y === 'number'
    ) {
      const { x, y } = parsed as { x: number; y: number };
      if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
    }
    return null;
  } catch {
    return null;
  }
};

/** position を保存. storage 不可 / quota 超過は silently drop. */
export const writeCursorEditorPopupPosition = (
  storage: StorageLike,
  pos: FloatingPopupPosition,
): void => {
  try {
    storage.setItem(CURSOR_EDITOR_POPUP_POS_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
  } catch {
    // 永続化できないだけで動作は継続させる.
  }
};
